import React from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import {
  Scissors, Gift, Home, Shirt, Sparkles, Utensils, Leaf, Briefcase, Factory, Package,
  UserRound, Target, Users, Megaphone, LineChart, Scale, Code, Palette, PartyPopper, HeartPulse,
} from "lucide-react";

const products = [
  { icon: Scissors, name: "Handmade & Artisan" },
  { icon: Gift, name: "Corporate Gifting" },
  { icon: Home, name: "Home & Living" },
  { icon: Shirt, name: "Fashion & Accessories" },
  { icon: Sparkles, name: "Beauty & Wellness" },
  { icon: Utensils, name: "Food & Beverage" },
  { icon: Leaf, name: "Sustainable Products" },
  { icon: Briefcase, name: "Office & Workplace" },
  { icon: Factory, name: "Manufacturing" },
  { icon: Package, name: "D2C Brands" },
];

const services = [
  { icon: UserRound, name: "Consultants" },
  { icon: Target, name: "Coaches & Trainers" },
  { icon: Users, name: "HR Professionals" },
  { icon: Megaphone, name: "Marketing & Branding" },
  { icon: LineChart, name: "Finance & Accounting" },
  { icon: Scale, name: "Legal Services" },
  { icon: Code, name: "Technology & IT" },
  { icon: Palette, name: "Creative Services" },
  { icon: PartyPopper, name: "Event Professionals" },
  { icon: HeartPulse, name: "Wellness Experts" },
];

function Grid({ items, prefix }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-testid={`${prefix}-grid`}>
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <motion.div
            key={it.name}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.04 }}
            className="bg-white border border-brand-line rounded-2xl p-5 flex flex-col items-start gap-3 hover:border-brand-wine/40 hover:-translate-y-1 hover:shadow-[0_16px_30px_-20px_rgba(139,30,63,0.25)] transition-all duration-300 cursor-default"
            data-testid={`category-${prefix}-${it.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          >
            <div className="w-10 h-10 rounded-full bg-brand-paper2 flex items-center justify-center">
              <Icon size={18} className="text-brand-wine" strokeWidth={1.6} />
            </div>
            <span className="font-serif text-lg font-medium text-brand-ink leading-tight">
              {it.name}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
}

export default function WhoCanJoin() {
  return (
    <section id="categories" className="py-24 md:py-32 bg-brand-cream" data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-brand-wine">
            Who Can Join
          </span>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-brand-ink">
            Built for women-led businesses <span className="italic text-brand-wine">across industries</span>.
          </h2>
          <p className="mt-6 text-lg text-brand-muted leading-relaxed">
            Whether you&apos;re just starting out or scaling up — you belong here.
          </p>
        </div>

        <Tabs defaultValue="products" className="mt-14">
          <TabsList className="mx-auto bg-transparent flex gap-2 md:gap-6 border-b border-brand-line rounded-none h-auto p-0 justify-center">
            <TabsTrigger
              value="products"
              className="font-serif text-xl md:text-2xl text-brand-muted pb-4 border-b-2 border-transparent transition-all data-[state=active]:border-brand-wine data-[state=active]:text-brand-wine data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold rounded-none px-6"
              data-testid="tab-products"
            >
              Products
            </TabsTrigger>
            <TabsTrigger
              value="services"
              className="font-serif text-xl md:text-2xl text-brand-muted pb-4 border-b-2 border-transparent transition-all data-[state=active]:border-brand-wine data-[state=active]:text-brand-wine data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:font-semibold rounded-none px-6"
              data-testid="tab-services"
            >
              Professional Services
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-10">
            <Grid items={products} prefix="products" />
          </TabsContent>
          <TabsContent value="services" className="mt-10">
            <Grid items={services} prefix="services" />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}
